"use client";
import Slider from "@/components/core/slider";
import { Trans } from "@lingui/react/macro";
import React from "react";
import { SwiperSlide } from "swiper/react";
import CategoryItem from "@/components/core/category";
import ProductItem from "@/components/core/product";
import HomepageSlider from "@/main/_components/layer-slider";
import { decode } from "html-entities";
import { handleNonListHTML } from "@/components/core/parse-custom-html";
import dynamic from "next/dynamic";
import LazyLoadSection from "./lazy-load-section";
import "../../stylesheet.css";

interface SectionData {
  type: string;
  section_name: string;
  items: [];
}
const Reviews = dynamic(() => import("./reviews"));

function HomeComponent({ lang, data }: { lang: string; data: any }) {
  const list = (item: SectionData) => {
    return (
      <div className="container mt-5 !px-0 md:mt-[50px]">
        <p className="mb-2 pl-[15px] text-[21px] font-bold md:text-[26px]">{item.section_name}</p>
        <Slider className="products-slider" spaceBetween={0}>
          {item?.items?.map((item: any, index: number) => (
            <SwiperSlide
              key={`product-${index}`}
              className="!mr-[0.8em] !h-auto !w-fit !pl-[15px] md:!mr-[0.5em]"
            >
              <ProductItem item={item} onHomePage={true} />
            </SwiperSlide>
          ))}
        </Slider>
      </div>
    );
  };
  const listCategories = (categories: SectionData) => {
    return (
      <div className="container mt-[30px]">
        <p className="mb-2 text-[21px] font-bold md:text-[26px]">
          <Trans>Top Category</Trans>
        </p>
        <Slider className="category-slider" spaceBetween={0}>
          {categories?.items.map((item: any, index: number) => (
            <SwiperSlide key={`cat-${index}`} style={{ width: "fit-content" }}>
              <CategoryItem item={item} />
            </SwiperSlide>
          ))}
        </Slider>
      </div>
    );
  };
  const listSubCategories = (subCategories: SectionData) => {
    const categories = subCategories?.items;
    return (
      <LazyLoadSection key="categorySlider">
        <div className="container mt-[30px] xs:mb-[50px]">
          <p className="mb-2 text-[21px] font-bold md:text-[26px]">
            <Trans>Categories</Trans>
          </p>
          <Slider className="category-slider" spaceBetween={0}>
            {categories.map((item: any, index: number) => {
              const categoryItem = categories?.at(index + 1);

              return (
                index % 2 !== 0 && (
                  <SwiperSlide
                    key={`subcat-${index}`}
                    style={{ width: "fit-content", display: "flex", flexDirection: "column" }}
                  >
                    <CategoryItem item={item} />
                    {categoryItem && <CategoryItem item={categoryItem} />}
                  </SwiperSlide>
                )
              );
            })}
          </Slider>
        </div>
      </LazyLoadSection>
    );
  };

  const extractReviews = (htmlString: string) => {
    if (typeof window !== "undefined") {
      const sanitizedHTML = decode(htmlString).replace(/src="([^"]+)"/g, ' data-src="$1" src="" ');

      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = sanitizedHTML;

      return [...tempDiv.querySelectorAll(".home-reviews--cards--item")].map((review) => {
        const name =
          review?.querySelector(".home-reviews--cards--item--name")?.textContent?.trim() ?? "";
        const image =
          review?.querySelector(".home-reviews--cards--item--picture")?.getAttribute("data-src") ??
          "";
        const location =
          review?.querySelector(".home-reviews--cards--item--title")?.textContent?.trim() ?? "";
        const reviewNumber = review?.querySelectorAll(
          ".home-reviews--cards--item--stars img",
        ).length; // Count stars
        const text =
          review.querySelector(".home-reviews--cards--item--message")?.textContent?.trim() ?? "";

        return { name, image, location, reviewNumber, text };
      });
    }
    return [];
  };

  const reviews = (content: any) => {
    const reviewArray = extractReviews(content);
    return (
      <div className="container mb-[50px] !px-0 md:mb-[100px]">
        <p className="mb-[25px] text-center text-[21px] font-bold md:text-[40px]">
          <Trans>Customer reviews</Trans>
        </p>
        <Reviews reviews={reviewArray} />
      </div>
    );
  };

  const renderSection = (content: any) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    switch (content.type) {
      case "layerslider":
        content.items.sections = Object.values(content.items.sections).filter((section: any) => {
          if (!section.end_date) return true; // If missing date, skip
          const start = new Date(section.start_date);
          const end = new Date(section.end_date);
          return today >= start && today <= end;
        });
        return <HomepageSlider key={content.section_name} item={content.items} lang={lang} />;
      case "cms":
        return list(content);
      case "category":
        return content.section_name === "Top Kategorien"
          ? listCategories(content)
          : listSubCategories(content);
      case "html":
        return content.section_name === "Customer Reviews" ? (
          <LazyLoadSection key="top-category">
            {reviews(content.content?.description)}
          </LazyLoadSection>
        ) : (
          <LazyLoadSection key={content.section_name}>
            {handleNonListHTML(content.content?.description, content.section_name)}
          </LazyLoadSection>
        );
    }
    return null;
  };

  return (
    <div className="overflow-x-hidden">
      {data?.data?.map((content: any) => (
        <React.Fragment key={content.section_name}>{renderSection(content)}</React.Fragment>
      ))}
    </div>
  );
}

export default React.memo(HomeComponent);
